/* eslint-disable no-nested-ternary */
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { typography, color, space } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import colors from 'theme/charts/colors';
import Line from 'theme/charts/line';
import Text from 'theme/text';
import History from 'theme/icons/history';
import translations from 'translations';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 83px;
`;

const Flex = styled.div`
  display: flex;
  flex-direction: row;
`;

const Card = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  align-items: center;

  & > .row {
    height: 75px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    flex: 1;
    gap: ${themeGet('space.xs')}px;
    min-width: 0;
    padding: ${themeGet('space.s')}px ${themeGet('space.s')}px;

    & > .left {
      min-width: 0;
      max-width: 55%;
      overflow: hidden;
    }

    .small {
      max-width: 200px;
    }

    @media (min-width: ${themeGet('breakpoints.0')}) {
      padding: ${themeGet('space.s')}px ${themeGet('space.m')}px;

      & > .left {
        max-width: 380px;
      }
    }

    & > .right {
      & > .right-row {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
      }

      & > .previous-revenues {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;

        & > p {
          padding-right: ${themeGet('space.s')}px;
        }
      }
    }
  }
`;

const WrapIcon = styled.span`
  ${color}
`;

const Value = styled.div`
  ${color}
  ${space}

  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const TextEllipsis = styled.p`
  ${typography}
  ${color}
  ${space}

  height: ${({ line }) => line * 22}px;
  overflow: hidden;
  text-overflow: ellipsis;
  /* stylelint-disable */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  /* stylelint-enable */
  -webkit-line-clamp: ${({ line }) => line};
`;

function Row({
  payload,
  index,
  languageCode,
  dataType,
  size,
  color = 'store_name',
  noPrevious = false,
}) {
  const colorLine = useMemo(() => colors[color][index % 6], [index]);
  const isUsage = dataType === 'usage_data';

  return (
    <Wrapper>
      <Card colorLine={colorLine}>
        <Text fontSize="display" lineHeight="display" fontWeight="medium" ml="m">
          {index + 1}
        </Text>
        <div className="row">
          <div className={`left ${size === 'small' && 'small'}`}>
            <Flex>
              <Text fontSize="h3" lineHeight="h3" fontWeight="bold" ml="xs" color={colorLine}>
                {isUsage
                  ? isNaN(payload.percentDuration)
                    ? 0
                    : payload.percentDuration
                  : isNaN(payload.percent)
                  ? 0
                  : payload.percent}
                %
              </Text>
              <TextEllipsis fontSize="h3" lineHeight="h3" fontWeight="bold" line="1" ml="xs">
                {payload.title}
              </TextEllipsis>
            </Flex>
            <Text fontSize="pSmall" lineHeight="pSmall" fontWeight="bold" ml="xs" color={colorLine}>
              {`${isUsage ? payload.totalCurrentDuration : payload.totalCurrentRevenues} (${
                payload.totalCurrentQuantity
              } ${translations[languageCode].units})`}
            </Text>
          </div>
          {!noPrevious && (
            <div className="right">
              <div className="right-row">
                <Text fontSize="label" lineHeight="label" fontWeight="normal" mr="xs">
                  {translations[languageCode].previous_period}
                </Text>

                <WrapIcon color={colorLine}>
                  <History />
                </WrapIcon>
              </div>

              <div className="previous-revenues">
                <Text fontSize="label" lineHeight="label" fontWeight="bold" color="grey_400">
                  {isUsage ? payload.totalPreviousDuration : payload.totalPreviousRevenues}
                </Text>
                {isUsage ? (
                  <Value>
                    <Text
                      fontSize="label"
                      lineHeight="label"
                      fontWeight="bold"
                      color={
                        payload.percentTrend > 0
                          ? 'lime_400'
                          : payload.percentTrend < 0
                          ? 'red_400'
                          : 'grey_600'
                      }
                      textAlign="right"
                    >
                      {`${
                        payload.percentTrend > 0 ? '+' : payload.percentTrend < 0 ? '-' : ''
                      } ${Math.abs(payload.percentTrend)}%`}
                    </Text>
                  </Value>
                ) : (
                  <Value>
                    <Text
                      fontSize="label"
                      lineHeight="label"
                      fontWeight="bold"
                      color={
                        payload.percentEarning > 0
                          ? 'lime_400'
                          : payload.percentEarning < 0
                          ? 'red_400'
                          : 'grey_600'
                      }
                      textAlign="right"
                    >
                      {`${
                        payload.percentEarning > 0 ? '+' : payload.percentEarning < 0 ? '-' : ''
                      } ${Math.abs(payload.percentEarning)}%`}
                    </Text>
                  </Value>
                )}
              </div>
            </div>
          )}
        </div>
      </Card>
      <Line
        percent={isUsage ? payload.percentDuration : payload.percent}
        defaultColor={colorLine}
      />
    </Wrapper>
  );
}

export default Row;
